// pages/CompetencyQuiz/index.js
//
// Round Training-1 (2026-10-26) — Competency Quiz (Training) module hub.
// "should be accessible by IT team and IT admin, they only can assign it to someone":
//
//   Take quiz   app.competencyQuiz       people IT assigns (+ IT Admin / IT Team)
//   Results     competencyQuiz.results   IT Admin, or "Competency Quiz - Results"
//   Assign      competencyQuiz.manage    IT Admin, or "Competency Quiz - Manage" (IT Team)
//   Questions   competencyQuiz.manage
//   Settings    competencyQuiz.manage
//
// Same shell as Access Management: one page, tabs filtered by module, only the
// active tab renders. The route itself is gated by competencyQuiz.any.
// /competency-quiz?tab=results opens that tab when visible.
import React, { useMemo, useState } from "react";
import { Card, CardBody, Container } from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useAuth from "../Personal Attendance/useAuth";
import { DashboardPageHeader, ResponsiveTabs } from "../../Components/Common/DashboardKit";
import TakeTab from "./TakeTab";
import ResultsTab from "./ResultsTab";
import AssignTab from "./AssignTab";
import QuestionsTab from "./QuestionsTab";
import SettingsTab from "./SettingsTab";

const TABS = [
    { key: "take",      label: "Take Quiz", icon: "ri-edit-box-line",       module: "app.competencyQuiz" },
    { key: "results",   label: "Results",   icon: "ri-bar-chart-box-line",  module: "competencyQuiz.results" },
    { key: "assign",    label: "Assign",    icon: "ri-user-add-line",       module: "competencyQuiz.manage" },
    { key: "questions", label: "Questions", icon: "ri-question-answer-line", module: "competencyQuiz.manage" },
    { key: "settings",  label: "Settings",  icon: "ri-settings-3-line",     module: "competencyQuiz.manage" },
];

export default function CompetencyQuiz() {
    document.title = "Training Quiz | SSW Technologies";

    const { isItAdmin, canAccessModule } = useAuth();
    const visibleTabs = useMemo(
        () => TABS.filter((t) => isItAdmin || canAccessModule(t.module)),
        [isItAdmin, canAccessModule]
    );
    const [activeTab, setActiveTab] = useState(() => {
        try {
            const wanted = new URLSearchParams(window.location.search).get("tab");
            if (wanted && visibleTabs.some((t) => t.key === wanted)) return wanted;
        } catch { /* ignore */ }
        return visibleTabs[0]?.key ?? "take";
    });
    const canSee = (key) => visibleTabs.some((t) => t.key === key);

    return (
        <div className="page-content">
            <ToastContainer position="top-right" autoClose={3500} />
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-survey-line"
                    eyebrow="Apps · Training"
                    title="Training Quiz"
                    tone="violet"
                    breadcrumb={[{ label: "Dashboards", to: "/dashboard" }, { label: "Training Quiz" }]}
                />

                <Card className="border-0 shadow-sm">
                    <CardBody>
                        <ResponsiveTabs tabs={visibleTabs} activeKey={activeTab} onChange={setActiveTab} color="primary" />

                        {visibleTabs.length === 0 && (
                            <div className="text-muted py-4 text-center">
                                The Training Quiz has not been assigned to you. Ask IT if you think it should be.
                            </div>
                        )}

                        <div className="pt-3">
                            {activeTab === "take" && canSee("take") && <TakeTab />}
                            {activeTab === "results" && canSee("results") && <ResultsTab />}
                            {activeTab === "assign" && canSee("assign") && <AssignTab />}
                            {activeTab === "questions" && canSee("questions") && <QuestionsTab />}
                            {activeTab === "settings" && canSee("settings") && <SettingsTab />}
                        </div>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
}
